import { useMemo } from 'react';
import { filterVisibleCategories } from '@/lib/tasks/visibleCategories';
/**
 * useTaskKanban - Hook para o Kanban de Tarefas
 * 
 * Gerencia categorias, fases (via RPCs) e movimentação de tarefas entre fases.
 * Segue padrão do useKanban (Conversas) adaptado para o módulo de tarefas.
 * 
 * @version 1.0.0
 * RELEASE: 2026-04-06 v1.0.0 - Criação inicial
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTaskCategories, type TaskCategory } from '@/hooks/useTaskCategories';
import { useTaskPhases, type TaskPhase } from '@/hooks/useTaskPhases';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

// Logger para depuração
const logger = createLogger('useTaskKanban');

/**
 * Filtros do Kanban de Tarefas
 */
export interface TaskKanbanFilters {
  status?: string;
  priority?: string;
  assigned_to?: string;
  search?: string;
  /** v1.8.0: Filtro por classificação */
  classification_id?: string;
  /** v1.8.0: Filtro por criador */
  created_by?: string;
  /** v1.8.0: Filtro prazo de/até */
  due_date_from?: string;
  due_date_to?: string;
  /** v1.8.0: Filtro data criação de/até */
  created_from?: string;
  created_to?: string;
  /** v1.8.0: Filtro data conclusão de/até */
  completed_from?: string;
  completed_to?: string;
  /** v1.9.0: Filtro "Sem Prazo" (TRUE = só sem due_date) */
  due_date_is_null?: boolean;
  /** v1.11.0: Data de solicitação (Retenção) / entrada (Jurídico) — de (YYYY-MM-DD) */
  request_from?: string;
  /** v1.11.0: Data de solicitação (Retenção) / entrada (Jurídico) — até (YYYY-MM-DD) */
  request_to?: string;
  /** v1.11.0: Faixa de período consumido (24h | 48h | 72h | 7d | 10d | 1m | 1m+) */
  period?: string;
  /** v1.11.0: Prazo processual (Jurídico) — de (YYYY-MM-DD) */
  legal_deadline_from?: string;
  /** v1.11.0: Prazo processual (Jurídico) — até (YYYY-MM-DD) */
  legal_deadline_to?: string;
}

/**
 * Item de tarefa retornado pela RPC get_task_kanban_column
 */
export interface TaskKanbanItem {
  id: string;
  number: number;
  title: string;
  description: string | null;
  priority: string;
  status: string;
  assigned_to: string | null;
  assigned_to_name: string | null;
  assigned_to_avatar: string | null;
  classification_name: string | null;
  classification_color: string | null;
  due_date: string | null;
  created_at: string;
  subtasks_count: number;
  subtasks_completed: number;
  comments_count: number;
  is_overdue: boolean;
  phase_id: string;
  /** v1.4.0: Data/hora de início da tarefa (null se pending) */
  started_at: string | null;
  /** v1.5.0: Se a tarefa está pausada */
  is_paused: boolean;
  /** v1.5.0: Tempo total de pausas finalizadas em segundos */
  total_pause_duration_seconds: number;
  /** v1.5.0: Início da pausa ativa (null se não pausada) */
  current_pause_started_at: string | null;
  /** v1.6.0: URL da primeira imagem anexada (capa do card estilo Trello) */
  cover_image_url: string | null;
  /** v1.6.0: Quantidade de anexos diretos da tarefa */
  attachments_count: number;
  /** v1.7.0: ID do contato vinculado */
  contact_id: string | null;
  /** v1.7.0: Nome do contato vinculado */
  contact_name: string | null;
  /** v1.7.0: ID do negócio vinculado */
  deal_id: string | null;
  /** v1.7.0: Título do negócio vinculado */
  deal_title: string | null;
  /** v1.10.0: Data/hora de conclusão — congela cronômetro no card */
  completed_at: string | null;
  /** v1.10.0: Data/hora de cancelamento — congela cronômetro no card */
  cancelled_at: string | null;
}

/**
 * Hook principal do Kanban de Tarefas
 */
export function useTaskKanban() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // Busca categorias ativas
  const { categories: allCategories, isLoading: isLoadingCategories } = useTaskCategories(true);
  // 2026-10-22: oculta abas sem nenhuma fase visível (regra calculada no servidor)
  const categories = useMemo(() => filterVisibleCategories(allCategories), [allCategories]);

  // Mutation para mover tarefa entre fases
  const moveTaskMutation = useMutation({
    mutationFn: async ({ taskId, newPhaseId }: { taskId: string; newPhaseId: string }) => {
      logger.debug('Movendo tarefa para nova fase', { taskId, newPhaseId });

      const { error } = await supabase.rpc('move_task_phase', {
        p_task_id: taskId,
        p_new_phase_id: newPhaseId,
        p_tenant_hint: currentTenant?.id || null,
      });

      if (error) {
        logger.error('Erro ao mover tarefa', { error: error.message });
        throw error;
      }
    },
    onSuccess: () => {
      // Invalida colunas e contagens para atualizar
      queryClient.invalidateQueries({ queryKey: ['task-kanban-column'] });
      queryClient.invalidateQueries({ queryKey: ['task-kanban-counts'] });
    },
    onError: (error: Error) => {
      toast.error(`Erro ao mover tarefa: ${error.message}`);
    },
  });

  return {
    categories,
    isLoadingCategories,
    moveTask: moveTaskMutation.mutateAsync,
    isMoving: moveTaskMutation.isPending,
  };
}
